import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Star, Gift, TrendingUp, Award, Loader2, ChevronRight, ShoppingBag, MessageSquare, Gem, Crown, Medal, Shield } from 'lucide-react';
import Footer from '../components/Footer';
import TopBar from '../components/TopBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  bronce: { icon: Shield, color: 'from-amber-700 to-amber-900', bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800' },
  plata: { icon: Medal, color: 'from-slate-400 to-slate-600', bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-700', badge: 'bg-slate-200 text-slate-700' },
  oro: { icon: Crown, color: 'from-yellow-400 to-amber-500', bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800' },
  diamante: { icon: Gem, color: 'from-cyan-400 to-blue-500', bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-800' }
};

const LoyaltyPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [member, setMember] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [view, setView] = useState('lookup'); // lookup | dashboard
  const [welcomeCoupon, setWelcomeCoupon] = useState(null); // 🎁 código 10% recién emitido
  const [welcomeCouponExpiry, setWelcomeCouponExpiry] = useState(null);
  const [couponCopied, setCouponCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/loyalty/config`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    setWelcomeCoupon(null);
    try {
      const res = await axios.get(`${API}/loyalty/member/${email.trim()}`);
      setMember(res.data);
      setView('dashboard');
    } catch (err) {
      if (err.response?.status === 404) {
        try {
          const reg = await axios.post(`${API}/loyalty/register?email=${encodeURIComponent(email.trim())}&name=`);
          if (reg.data?.welcome_coupon) {
            setWelcomeCoupon(reg.data.welcome_coupon);
            setWelcomeCouponExpiry(reg.data.welcome_coupon_expiry || null);
          }
          const res = await axios.get(`${API}/loyalty/member/${email.trim()}`);
          setMember(res.data);
          setView('dashboard');
        } catch {
          setError('Error al registrar. Inténtalo de nuevo.');
        }
      } else {
        setError('Error al consultar. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyWelcomeCoupon = async () => {
    if (!welcomeCoupon) return;
    try {
      await navigator.clipboard.writeText(welcomeCoupon);
      setCouponCopied(true);
      setTimeout(() => setCouponCopied(false), 2500);
    } catch {}
  };

  const handleRedeem = async (points) => {
    setRedeemLoading(true);
    setRedeemResult(null);
    try {
      const res = await axios.post(`${API}/loyalty/redeem?email=${encodeURIComponent(member.email)}&points_to_use=${points}`);
      setRedeemResult(res.data);
      const updated = await axios.get(`${API}/loyalty/member/${member.email}`);
      setMember(updated.data);
    } catch (err) {
      setRedeemResult({ error: err.response?.data?.detail || 'Error al canjear puntos' });
    } finally {
      setRedeemLoading(false);
    }
  };

  const tierStyle = member?.tier_info ? TIER_CONFIG[member.tier_info.id] || TIER_CONFIG.bronce : TIER_CONFIG.bronce;
  const TierIcon = tierStyle.icon;

  const progressPercent = member?.next_tier
    ? Math.min(100, ((member.total_points - (member.tier_info?.min || 0)) / ((member.next_tier?.min || 1) - (member.tier_info?.min || 0))) * 100)
    : 100;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <TopBar />

      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <Button onClick={() => navigate('/')} variant="ghost" className="mb-6" data-testid="loyalty-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black mb-4">
            <Star className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="loyalty-title">Programa de Fidelidad</h1>
          <p className="text-base text-gray-600 mt-2">Acumula puntos con cada compra y canjéalos por descuentos</p>
        </div>

        {view === 'lookup' ? (
          <>
            {/* Lookup Form */}
            <Card className="p-6 mb-8 max-w-md mx-auto" data-testid="loyalty-lookup-card">
              <h2 className="text-lg font-bold mb-1 text-center">Consulta tus puntos</h2>
              <p className="text-xs text-center text-neutral-600 mb-4">
                <span className="inline-block bg-black text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest mr-1">Nuevo</span>
                Regístrate y recibe un <strong>10% de descuento</strong> de bienvenida 🎁
              </p>
              <form onSubmit={handleLookup} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Tu email de compra"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="loyalty-email-input"
                />
                <Button type="submit" disabled={loading} className="w-full bg-black hover:bg-neutral-800" data-testid="loyalty-lookup-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
                  {loading ? 'Consultando...' : 'Ver mis puntos'}
                </Button>
                {error && <p className="text-red-500 text-sm text-center" data-testid="loyalty-error">{error}</p>}
              </form>
              <p className="text-xs text-gray-500 mt-3 text-center">Si es tu primera vez, te registraremos automáticamente</p>
            </Card>

            {/* How it works */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-center mb-6">Cómo funciona</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-5 text-center hover:shadow-lg transition-shadow">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-black" />
                  <h3 className="font-bold text-gray-800 mb-1">Compra</h3>
                  <p className="text-sm text-gray-600">Gana {config?.points_per_euro || 1} punto por cada 1€ gastado</p>
                </Card>
                <Card className="p-5 text-center hover:shadow-lg transition-shadow">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 text-black" />
                  <h3 className="font-bold text-gray-800 mb-1">Deja reseñas</h3>
                  <p className="text-sm text-gray-600">+{config?.review_points || 20} puntos por cada reseña</p>
                </Card>
                <Card className="p-5 text-center hover:shadow-lg transition-shadow">
                  <Gift className="w-10 h-10 mx-auto mb-3 text-green-500" />
                  <h3 className="font-bold text-gray-800 mb-1">Canjea</h3>
                  <p className="text-sm text-gray-600">{config?.points_to_redeem || 100} pts = {config?.discount_per_redemption || 5}€ descuento</p>
                </Card>
              </div>
            </div>

            {/* Tier explanation */}
            <div>
              <h2 className="text-xl font-bold text-center mb-6">Niveles de fidelidad</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(config?.tiers || TIER_CONFIG).map(([key, tier]) => {
                  const style = TIER_CONFIG[key] || TIER_CONFIG.bronce;
                  const Icon = style.icon;
                  return (
                    <Card key={key} className={`p-4 ${style.border} border-2 hover:shadow-lg transition-shadow`} data-testid={`tier-card-${key}`}>
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r ${style.color} mb-3`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className={`font-bold ${style.text}`}>{tier.label || key}</h3>
                      <p className="text-xs text-gray-500 mb-2">{tier.min || 0} - {tier.max === 999999 ? '∞' : tier.max} pts</p>
                      {tier.discount > 0 && <p className="text-xs font-semibold text-green-600">+{tier.discount}% descuento extra</p>}
                      {tier.free_shipping && <p className="text-xs font-semibold text-blue-600">Envío gratis siempre</p>}
                    </Card>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          /* Dashboard */
          <>
            <Button onClick={() => { setView('lookup'); setMember(null); setRedeemResult(null); setWelcomeCoupon(null); setWelcomeCouponExpiry(null); }} variant="ghost" className="mb-4 text-sm">
              <ArrowLeft className="w-3 h-3 mr-1" /> Consultar otro email
            </Button>

            {/* 🎁 Welcome coupon banner — solo aparece cuando el usuario acaba de registrarse */}
            {welcomeCoupon && (
              <Card
                className="relative overflow-hidden p-6 mb-6 border-2 border-black bg-black text-white"
                data-testid="welcome-coupon-banner"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/5 blur-2xl"
                />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center flex-shrink-0">
                    <Gift className="w-7 h-7" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 mb-1">
                      Regalo de bienvenida
                    </p>
                    <h3 className="text-2xl font-black uppercase tracking-tight mb-1">
                      10% de descuento
                    </h3>
                    <p className="text-sm text-white/70">
                      Aplica este código en el checkout. Un solo uso.
                      {welcomeCouponExpiry && (
                        <>
                          {' · Caduca el '}
                          <strong className="text-white">
                            {new Date(welcomeCouponExpiry).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </strong>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-stretch sm:items-end gap-2">
                    <div
                      className="px-5 py-3 border-2 border-dashed border-white/40 font-mono text-lg font-bold tracking-widest text-center select-all"
                      data-testid="welcome-coupon-code"
                    >
                      {welcomeCoupon}
                    </div>
                    <button
                      onClick={copyWelcomeCoupon}
                      className="text-xs font-bold uppercase tracking-[0.2em] bg-white text-black px-4 py-2 hover:bg-neutral-200 transition-colors"
                      data-testid="copy-welcome-coupon"
                    >
                      {couponCopied ? '✓ Copiado' : 'Copiar código'}
                    </button>
                  </div>
                </div>
                <p className="relative text-[11px] text-white/50 mt-4">
                  También te lo enviamos a <strong className="text-white/80">{member?.email}</strong>. Revisa tu bandeja de entrada (y spam).
                </p>
              </Card>
            )}

            {/* Tier Card */}
            <Card className={`p-6 mb-6 border-2 ${tierStyle.border} ${tierStyle.bg}`} data-testid="loyalty-tier-card">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${tierStyle.color} flex items-center justify-center`}>
                    <TierIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tu nivel</p>
                    <h2 className={`text-2xl font-bold ${tierStyle.text}`} data-testid="loyalty-tier-name">{member?.tier_info?.label}</h2>
                    {member?.tier_info?.discount > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${tierStyle.badge}`}>+{member.tier_info.discount}% descuento</span>
                    )}
                    {member?.tier_info?.free_shipping && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 ml-1">Envío gratis</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-gray-900" data-testid="loyalty-points">{member?.available_points}</p>
                  <p className="text-sm text-gray-500">puntos disponibles</p>
                </div>
              </div>

              {/* Progress to next tier */}
              {member?.next_tier && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{member.tier_info?.label}</span>
                    <span>{member.next_tier.label} (faltan {member.next_tier.points_needed} pts)</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${tierStyle.color} transition-all duration-500`} style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>
              )}
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{member?.total_points}</p>
                <p className="text-xs text-gray-500">Puntos totales</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{member?.redeemed_points}</p>
                <p className="text-xs text-gray-500">Puntos canjeados</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{member?.orders_count}</p>
                <p className="text-xs text-gray-500">Compras</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{member?.total_spent?.toFixed(0)}€</p>
                <p className="text-xs text-gray-500">Total gastado</p>
              </Card>
            </div>

            {/* Redeem */}
            <Card className="p-6 mb-6" data-testid="loyalty-redeem-card">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-500" /> Canjear puntos
              </h3>
              {member?.available_points >= (config?.points_to_redeem || 100) ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Cada {config?.points_to_redeem || 100} puntos = {config?.discount_per_redemption || 5}€ de descuento
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4].map(mult => {
                      const pts = mult * (config?.points_to_redeem || 100);
                      const discount = mult * (config?.discount_per_redemption || 5);
                      if (pts > member.available_points) return null;
                      return (
                        <Button
                          key={mult}
                          onClick={() => handleRedeem(pts)}
                          disabled={redeemLoading}
                          variant="outline"
                          className="border-green-300 hover:bg-green-50"
                          data-testid={`redeem-${pts}-btn`}
                        >
                          {redeemLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : `${pts} pts = ${discount}€`}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Necesitas al menos {config?.points_to_redeem || 100} puntos para canjear. Te faltan {(config?.points_to_redeem || 100) - (member?.available_points || 0)} puntos.
                </p>
              )}

              {redeemResult && (
                <div className={`mt-4 p-3 rounded-lg ${redeemResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="redeem-result">
                  {redeemResult.error ? (
                    <p className="text-sm">{redeemResult.error}</p>
                  ) : (
                    <div>
                      <p className="font-bold">{redeemResult.message}</p>
                      <p className="text-sm mt-1">Te hemos enviado el código de descuento a tu email.</p>
                      <p className="text-xs mt-1">Úsalo en tu carrito para aplicar el descuento</p>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Transaction History */}
            <Card className="p-6" data-testid="loyalty-history-card">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-black" /> Historial
              </h3>
              {member?.transactions?.length > 0 ? (
                <div className="space-y-2">
                  {member.transactions.map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'earn' ? 'bg-green-100' : 'bg-red-100'}`}>
                          {tx.type === 'earn' ? <TrendingUp className="w-4 h-4 text-green-600" /> : <Gift className="w-4 h-4 text-red-600" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{tx.description}</p>
                          <p className="text-xs text-gray-400">{new Date(tx.created_at).toLocaleDateString('es-ES')}</p>
                        </div>
                      </div>
                      <span className={`font-bold text-sm ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.points > 0 ? '+' : ''}{tx.points}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Aún no tienes movimientos. ¡Haz tu primera compra!</p>
              )}
            </Card>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LoyaltyPage;
